/**
 * Client-side image compression helpers.
 *
 * Why: vehicle photos are stored as base64 data-URLs in MongoDB. Raw
 * camera uploads are 3-5 MB each and cause:
 *   • Slow public scan page render (security guards on phones)
 *   • Mongo bloat (~33% base64 overhead × 4 photos × N vehicles)
 *   • Slow API responses
 *
 * compressImage() resizes the longest edge to MAX_DIM and re-encodes as JPEG
 * at quality 0.78 — typically reduces a 4 MB iPhone photo to ~200 KB while
 * keeping enough detail for license-plate / make-model verification.
 */
const MAX_DIM = 1280; // px on the longest edge
const QUALITY = 0.78; // JPEG quality
const MIME = "image/jpeg";

/**
 * Compress a File or Blob to a base64 data-URL.
 * Falls back to FileReader if the canvas pipeline fails (e.g. unsupported
 * format) — better to upload a too-large image than to fail silently.
 */
export async function compressImage(file) {
  if (!(file instanceof Blob)) return null;
  // Browsers can't decode HEIC; let the user retry with JPEG/PNG.
  if (file.type === "image/heic" || file.type === "image/heif") {
    return readAsDataURL(file);
  }
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, MAX_DIM / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close && bitmap.close();
    return canvas.toDataURL(MIME, QUALITY);
  } catch {
    return readAsDataURL(file);
  }
}

function readAsDataURL(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
